/* [ START OF MODULE - VER 1.0 (src/components/TosGate.jsx) ] */
/**
 * TosGate — full-screen Terms of Service acceptance gate shown after login,
 * before any app routing, until the user accepts the current published version.
 * BUILD-2026.498 / RMI-2026-256.
 *
 * Acceptance POSTs to /api/tos-accept, which validates the session and stamps
 * IP + timestamp server-side (the browser cannot forge those). On success we
 * call onAccepted() so the gate re-checks and the app proceeds.
 */
import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { ShieldCheck, LogOut } from 'lucide-react';

export default function TosGate({ doc, onAccepted, onLogout }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session && session.access_token;
      if (!token) throw new Error('Your session expired — please log in again.');
      const resp = await fetch('/api/tos-accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ doc_type: 'tos', doc_version: doc && doc.version }),
      });
      const out = await resp.json();
      if (!out.ok) throw new Error(out.error || 'Could not record your acceptance.');
      if (onAccepted) onAccepted();
    } catch (e) {
      setError((e && e.message) || 'Something went wrong.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', flexDirection: 'column', background: 'var(--bg-main)', fontFamily: "'Poppins', sans-serif" }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0 24px', height: '60px', background: 'var(--bg-sidebar)', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        <ShieldCheck size={20} style={{ color: 'var(--accent-primary)' }} />
        <span style={{ fontWeight: 900, fontSize: '0.95rem', color: 'var(--text-primary)', letterSpacing: '0.02em' }}>{(doc && doc.title) || 'Terms of Service'}</span>
        <button onClick={onLogout} title="Log out" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.05em', cursor: 'pointer' }}>
          <LogOut size={12} /> LOG OUT
        </button>
      </header>

      <div className="custom-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto', width: '100%' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto', padding: '28px 24px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Please review and accept to continue{doc && doc.version ? ' \u00b7 v' + doc.version : ''}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.65, color: 'var(--text-secondary)' }}>
            {(doc && doc.body) || 'Terms are unavailable right now. Please try again shortly.'}
          </div>
        </div>
      </div>

      <footer style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-sidebar)', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
        {error ? <div style={{ color: 'var(--accent-danger)', fontSize: '0.8rem', fontWeight: 700 }}>{error}</div> : null}
        <label style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '0.85rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
          I have read and agree to the Terms of Service.
        </label>
        <button onClick={accept} disabled={!checked || submitting}
          style={{
            display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 22px', borderRadius: '9px', border: 'none',
            background: (!checked || submitting) ? 'var(--bg-input)' : 'var(--accent-primary)',
            color: (!checked || submitting) ? 'var(--text-muted)' : '#fff',
            fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.03em',
            cursor: (!checked || submitting) ? 'not-allowed' : 'pointer', transition: '0.15s',
          }}>
          <ShieldCheck size={15} /> {submitting ? 'Recording\u2026' : 'Accept & Continue'}
        </button>
      </footer>
    </div>
  );
}
/* [ END OF MODULE - VER 1.0 (src/components/TosGate.jsx) ] */
